import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  ChevronsUpDown,
  CircleCheck,
  Download,
  FileText,
  Loader2,
  Send,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Hint } from '@/components/ui/hint';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/sonner';
import { FieldError, FieldLabel, TextField } from '@/components/FormField';
import { DateField, DateRangeField } from '@/components/DateField';
import { usePageTitle } from '@/components/layout/page-title';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ApiError } from '@/features/auth/api';
import { useIssuerProfile } from '@/features/issuer/api';
import { AmountPill } from '@/features/invoices/AmountPill';
import { formatAccountFull, formatMoney } from '@/features/invoices/format';
import { cn } from '@/lib/utils';
import {
  INVOICE_DRAFTS_QUERY_KEY,
  V1_SELECTABLE_PAYMENT_METHODS,
  useDeleteDraft,
  useDraft,
  type DraftDetail,
  type Finding,
  type PaymentMethod,
  type TaxScheme,
} from './api';
import { BuyerCard } from './BuyerCard';
import { type BuyerRef } from './BuyerPicker';
import {
  contentFromEditorState,
  editorStateFromContent,
  emptyAdditionalDataDraft,
  emptyEditorState,
  emptyLineDraft,
  hasExemptLine,
  type AdditionalDataDraft,
  type EditorState,
  type LineDraft,
} from './content';
import { InvoiceNumberHeading } from './InvoiceNumberHeading';
import { IssueConfirmDialog } from './IssueConfirmDialog';
import {
  ADDITIONAL_DATA_ROW_ERROR_CODES,
  additionalDataErrorsByKey,
  fieldErrorKey,
  LINE_ERROR_CODES,
  lineErrorsByKey,
} from './fieldErrors';
import { AdditionalDataEditor } from './AdditionalDataEditor';
import { LinesEditor, MarginProcedurePicker } from './LinesEditor';
import { downloadDraftXml, previewDraftPdf } from './previewPdf';
import { useDraftAutosave } from './useDraftAutosave';

/**
 * The invoice composer, serving both `/invoices/new/standard` (a not-yet-created draft, reached from the
 * `/invoices/new` type picker) and `/invoices/drafts/:id` (an existing one).
 *
 * A "new" invoice is composed entirely in memory — no server row is created on entry. The first
 * meaningful edit lazily creates the draft (see {@link useDraftAutosave}); we then swap the URL
 * `/invoices/new/standard` → `/invoices/drafts/{id}` (replace) *without* tearing the composer down, so the edits
 * stay in local state and ride the first save. Empty rows are never created, so there's nothing to prune.
 *
 * A composing "session" is keyed so that create-driven URL swap does NOT remount the composer (which
 * would reseed it from the server mid-edit), while genuinely opening a different draft — or hitting
 * "New" again — starts a fresh session.
 */
export function InvoiceComposerPage() {
  const { t } = useTranslation();
  usePageTitle(t('invoices.compose.title'));
  const { id: idParam } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // A brand-new invoice takes its scheme from the type-picker route it was opened from; an existing
  // draft carries its own (it is fixed for the draft's life, since it reshapes the whole form).
  const routeScheme: TaxScheme = pathname.endsWith('/new/standard-margin') ? 'MARGIN' : 'REGULAR';

  const valid = idParam == null || /^\d+$/.test(idParam);
  const routeId = valid && idParam != null ? Number(idParam) : null;

  const routeIdRef = useRef(routeId);
  routeIdRef.current = routeId;
  // The id this instance just created, so we recognise our own create-driven URL swap and don't treat
  // it as a request to open a different draft.
  const createdIdRef = useRef<number | null>(null);
  // `token` bumps to force a fresh composer; `entryId` is the draft to seed from (null = in-memory new);
  // `scheme` is the new draft's scheme, pinned at session start. Pinning matters because the create-driven
  // swap to `/invoices/drafts/{id}` leaves this session alive: re-reading the scheme off the live pathname
  // would silently demote a margin composer to REGULAR the moment its row is born.
  const [session, setSession] = useState(() => ({ token: 0, entryId: routeId, scheme: routeScheme }));

  useEffect(() => {
    if (!valid) return;
    if (routeId != null && routeId === createdIdRef.current) return; // our own create's URL swap
    // Nothing changed — same draft, or (for a new one) the same type-picker route.
    const sameEntry =
      routeId === session.entryId && (routeId != null || routeScheme === session.scheme);
    if (sameEntry && createdIdRef.current == null) return;
    createdIdRef.current = null;
    setSession((s) => ({ token: s.token + 1, entryId: routeId, scheme: routeScheme }));
  }, [routeId, valid, routeScheme, session.entryId, session.scheme]);

  const handleCreated = useCallback(
    (createdId: number) => {
      createdIdRef.current = createdId;
      navigate(`/invoices/drafts/${createdId}`, { replace: true });
    },
    [navigate],
  );

  // Conflict recovery: refetch the current row fresh, then restart the session so the composer reseeds
  // from the authoritative version.
  const handleReload = useCallback(() => {
    const id = routeIdRef.current;
    if (id == null) return;
    void qc.refetchQueries({ queryKey: [...INVOICE_DRAFTS_QUERY_KEY, 'detail', id] }).then(() => {
      createdIdRef.current = null;
      setSession((s) => ({ ...s, token: s.token + 1, entryId: id }));
    });
  }, [qc]);

  if (!valid) return <Navigate to="/invoices/drafts" replace />;

  return (
    <ComposerSession
      key={session.token}
      entryId={session.entryId}
      routeScheme={session.scheme}
      onCreated={handleCreated}
      onReload={handleReload}
    />
  );
}

/** Resolves a session to a form: a new draft renders immediately; an existing one loads first. */
function ComposerSession({
  entryId,
  routeScheme,
  onCreated,
  onReload,
}: {
  entryId: number | null;
  routeScheme: TaxScheme;
  onCreated: (id: number) => void;
  onReload: () => void;
}) {
  if (entryId == null) {
    return (
      <ComposerForm
        initial={null}
        scheme={routeScheme}
        onCreated={onCreated}
        onReload={onReload}
      />
    );
  }
  return <ExistingDraft id={entryId} onCreated={onCreated} onReload={onReload} />;
}

/** Loads an existing draft (id from the URL) and, once ready, hands it to the shared composer form. */
function ExistingDraft({
  id,
  onCreated,
  onReload,
}: {
  id: number;
  onCreated: (id: number) => void;
  onReload: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useDraft(id);

  if (query.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
        <p className="text-sm text-destructive">{t('invoices.compose.load_error')}</p>
        <Button variant="outline" onClick={() => navigate('/invoices/drafts')}>
          {t('invoices.compose.back_to_drafts')}
        </Button>
      </div>
    );
  }

  return (
    <ComposerForm
      initial={query.data}
      scheme={query.data.draft.taxScheme}
      onCreated={onCreated}
      onReload={onReload}
    />
  );
}

function ComposerForm({
  initial,
  scheme,
  onCreated,
  onReload,
}: {
  /** The loaded draft when editing an existing row; null when composing a brand-new invoice in memory. */
  initial: DraftDetail | null;
  /** How the tax is accounted for — from the loaded draft, or from the type-picker route for a new one. */
  scheme: TaxScheme;
  onCreated: (id: number) => void;
  onReload: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? i18n.language;
  const navigate = useNavigate();

  // The draft id once it exists — null while composing a new invoice in memory. Adopted when the lazy
  // create lands, which also gates the actions that need a saved row (preview, XML, issue).
  const [draftId, setDraftId] = useState<number | null>(initial?.draft.id ?? null);

  const lineKey = useRef(0);
  const nextKey = () => lineKey.current++;
  const [state, setState] = useState<EditorState>(() =>
    initial != null
      ? editorStateFromContent(initial.draft.content, nextKey)
      : emptyEditorState(nextKey),
  );
  const [buyer, setBuyer] = useState<BuyerRef | null>(
    initial?.draft.customerId != null
      ? {
          id: initial.draft.customerId,
          label: initial.draft.buyerLabel ?? t('invoices.compose.no_buyer'),
        }
      : null,
  );
  const [previewPending, setPreviewPending] = useState(false);
  const [xmlPending, setXmlPending] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);

  const content = useMemo(() => contentFromEditorState(state), [state]);
  const autosave = useDraftAutosave({
    content,
    customerId: buyer?.id ?? null,
    taxScheme: scheme,
    initial,
    lineKeys: state.lines.map((l) => l.key),
    additionalDataKeys: state.additionalData.map((r) => r.id),
    onCreated: (id) => {
      setDraftId(id);
      onCreated(id);
    },
    onCreateFailed: () => toast.error(t('invoices.compose.errors.create_failed')),
  });
  const { evaluation, evaluationLineKeys, evaluationAdditionalDataKeys, conflict, readOnly, saving } =
    autosave;
  const disabled = conflict || readOnly;
  const del = useDeleteDraft();

  // Errors come back keyed by 1-based line index; re-key them onto stable line keys (via the line
  // order that produced this evaluation) so a drag-reorder doesn't strand a mark on the wrong row.
  const lineErrors = useMemo(
    () => lineErrorsByKey(evaluation.fieldErrors, evaluationLineKeys),
    [evaluation.fieldErrors, evaluationLineKeys],
  );
  const additionalDataErrors = useMemo(
    () => additionalDataErrorsByKey(evaluation.fieldErrors, evaluationAdditionalDataKeys),
    [evaluation.fieldErrors, evaluationAdditionalDataKeys],
  );

  const margin = scheme === 'MARGIN';
  const profileNotReady = evaluation.fieldErrors.some((e) => e.code === 'PROFILE_NOT_READY');
  const noBuyer = evaluation.fieldErrors.some((e) => e.code === 'NO_BUYER');
  // A margin invoice has no rates, so it can never carry an exempt line or need an exemption basis.
  const showExemption = !margin && hasExemptLine(state);
  const marginProcedureError = evaluation.fieldErrors.some(
    (e) => e.code === 'MARGIN_PROCEDURE_MISSING',
  )
    ? t(fieldErrorKey('MARGIN_PROCEDURE_MISSING'))
    : undefined;
  const footerNoteError = evaluation.fieldErrors.some((e) => e.code === 'FOOTER_NOTE_TOO_LONG')
    ? t(fieldErrorKey('FOOTER_NOTE_TOO_LONG'))
    : undefined;

  // Every outstanding problem, for the "not ready" state hover: field errors (line-scoped ones
  // prefixed with their *current* position) followed by the rule engine's blockers and warnings.
  const findingText = (f: Finding) => (lng.startsWith('pl') ? f.messagePl : f.messageEn);
  const currentPosition = (line: number) => {
    const key = evaluationLineKeys[line - 1];
    const index = key != null ? state.lines.findIndex((l) => l.key === key) : -1;
    return index >= 0 ? index + 1 : line;
  };
  const issues = [
    ...evaluation.fieldErrors.map((e) => {
      const message = t(fieldErrorKey(e.code));
      if (e.line != null && LINE_ERROR_CODES.has(e.code)) {
        return `${t('invoices.compose.issues.line', { n: currentPosition(e.line) })}: ${message}`;
      }
      if (e.line != null && ADDITIONAL_DATA_ROW_ERROR_CODES.has(e.code)) {
        return `${t('invoices.compose.issues.additional_data_row', { n: e.line })}: ${message}`;
      }
      return message;
    }),
    ...evaluation.validation.blockers.map(findingText),
    ...evaluation.validation.warnings.map(findingText),
  ];

  const patch = (p: Partial<EditorState>) => setState((s) => ({ ...s, ...p }));
  const patchLine = (key: number, p: Partial<LineDraft>) =>
    setState((s) => ({ ...s, lines: s.lines.map((l) => (l.key === key ? { ...l, ...p } : l)) }));
  const addLine = () => setState((s) => ({ ...s, lines: [...s.lines, emptyLineDraft(nextKey())] }));
  const removeLine = (key: number) =>
    setState((s) => ({
      ...s,
      lines: s.lines.length === 1 ? s.lines : s.lines.filter((l) => l.key !== key),
    }));
  const moveLine = (fromKey: number, toKey: number) =>
    setState((s) => {
      const from = s.lines.findIndex((l) => l.key === fromKey);
      const to = s.lines.findIndex((l) => l.key === toKey);
      if (from < 0 || to < 0 || from === to) return s;
      const lines = [...s.lines];
      const [moved] = lines.splice(from, 1);
      if (moved === undefined) return s;
      lines.splice(to, 0, moved);
      return { ...s, lines };
    });

  const patchAdditional = (id: number, p: Partial<AdditionalDataDraft>) =>
    setState((s) => ({
      ...s,
      additionalData: s.additionalData.map((r) => (r.id === id ? { ...r, ...p } : r)),
    }));
  const addAdditional = () =>
    setState((s) => ({
      ...s,
      additionalData: [...s.additionalData, emptyAdditionalDataDraft(nextKey())],
    }));
  const removeAdditional = (id: number) =>
    setState((s) => ({ ...s, additionalData: s.additionalData.filter((r) => r.id !== id) }));

  const onPreview = () => {
    if (draftId == null) return;
    setPreviewPending(true);
    previewDraftPdf({
      id: draftId,
      watermark: t('invoices.compose.preview.watermark'),
      flush: autosave.flush,
    })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.code === 'DRAFT_NOT_RENDERABLE') {
          toast.error(t('invoices.compose.preview.not_renderable'));
        } else {
          toast.error(t('invoices.compose.preview.failed'));
        }
      })
      .finally(() => setPreviewPending(false));
  };

  const onDownloadXml = () => {
    if (draftId == null) return;
    setXmlPending(true);
    downloadDraftXml({ id: draftId, flush: autosave.flush })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.code === 'DRAFT_NOT_RENDERABLE') {
          toast.error(t('invoices.compose.preview.not_renderable'));
        } else {
          toast.error(t('invoices.compose.preview.failed'));
        }
      })
      .finally(() => setXmlPending(false));
  };

  const onDelete = () => {
    if (draftId == null) return;
    // Stop autosave first: the navigate-away unmount flush would otherwise PUT (or lazily recreate)
    // the very row we're deleting.
    autosave.stop();
    del.mutate(draftId, {
      onSuccess: () => {
        toast.success(t('invoices.drafts.toasts.deleted'));
        navigate('/invoices/drafts');
      },
      onError: () => toast.error(t('invoices.drafts.toasts.delete_failed')),
    });
  };

  // preview-xml 422s unless the draft assembles (no field errors) and needs a saved row, so gate on
  // that + editability + the draft actually existing yet (a brand-new invoice has no row to render).
  const previewDisabled = disabled || draftId == null || evaluation.fieldErrors.length > 0;

  // The number the invoice will carry: a manual override when present, else the predicted next number.
  const trimmedOverride = state.numberOverride.trim();
  const effectiveNumber = trimmedOverride !== '' ? trimmedOverride : evaluation.suggestedNumber;
  // Issue is the fiscal action — only offered once the draft exists, is fully issuable, and is still
  // editable (a conflicted / read-only draft can't be issued from here).
  const canIssue = draftId != null && evaluation.isIssuable && !disabled;

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <InvoiceNumberHeading
            suggestedNumber={evaluation.suggestedNumber}
            value={state.numberOverride}
            onChange={(value) => patch({ numberOverride: value })}
            disabled={disabled}
          />
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
            {draftId != null ? <SaveStatus saving={saving} disabled={disabled} /> : null}
            <StateChip issuable={evaluation.isIssuable} issues={issues} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {draftId != null ? (
            <ConfirmDialog
              title={t('invoices.drafts.confirm.delete_title')}
              description={t('invoices.drafts.confirm.delete_description')}
              confirmLabel={t('invoices.drafts.delete')}
              destructive
              onConfirm={onDelete}
            >
              <Button
                type="button"
                variant="destructive"
                disabled={readOnly || del.isPending}
              >
                {del.isPending ? (
                  <Loader2 data-icon="inline-start" className="animate-spin" />
                ) : (
                  <Trash2 data-icon="inline-start" />
                )}
                {t('invoices.drafts.delete')}
              </Button>
            </ConfirmDialog>
          ) : null}
          <Button
            type="button"
            variant="outline"
            disabled={previewDisabled || previewPending}
            onClick={onPreview}
          >
            {previewPending ? (
              <Loader2 data-icon="inline-start" className="animate-spin" />
            ) : (
              <FileText data-icon="inline-start" />
            )}
            {t('invoices.details.generate_pdf')}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={previewDisabled || xmlPending}
            onClick={onDownloadXml}
          >
            {xmlPending ? (
              <Loader2 data-icon="inline-start" className="animate-spin" />
            ) : (
              <Download data-icon="inline-start" />
            )}
            {t('invoices.details.download_xml')}
          </Button>
          <Button
            type="button"
            disabled={!canIssue}
            onClick={() => setIssueOpen(true)}
          >
            <Send data-icon="inline-start" />
            {t('invoices.compose.issue.action')}
          </Button>
        </div>
      </div>

      {draftId != null ? (
        <IssueConfirmDialog
          open={issueOpen}
          onOpenChange={setIssueOpen}
          draftId={draftId}
          invoiceNumber={effectiveNumber}
          warnings={evaluation.validation.warnings}
          flush={autosave.flush}
        />
      ) : null}

      {profileNotReady ? <ProfileNotReadyBanner /> : null}
      {conflict ? <ConflictBanner onReload={onReload} /> : null}
      {readOnly && !conflict ? <ReadOnlyBanner /> : null}

      <ScrollArea className="cn-vertical-scroll -mr-3 min-h-0 flex-1">
        <div className="space-y-6 pr-3">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <BuyerCard buyer={buyer} onSelect={setBuyer} disabled={disabled} invalid={noBuyer} />

            <SectionCard title={t('invoices.compose.sections.sale_date')}>
              <SaleDateFields
                state={state}
                margin={margin}
                procedureError={marginProcedureError}
                disabled={disabled}
                patch={patch}
              />
            </SectionCard>

            <SectionCard title={t('invoices.compose.sections.payment')}>
              <PaymentFields state={state} disabled={disabled} patch={patch} />
            </SectionCard>
          </div>

          <SectionCard title={t('invoices.compose.sections.lines')}>
            <LinesEditor
              lines={state.lines}
              lineErrors={lineErrors}
              scheme={scheme}
              disabled={disabled}
              onPatch={patchLine}
              onAdd={addLine}
              onRemove={removeLine}
              onMove={moveLine}
            />
            {showExemption ? (
              <TextField
                id="exemption-basis"
                className="mt-3"
                label={t('invoices.compose.fields.exemption_basis')}
                value={state.exemptionLegalBasis}
                disabled={disabled}
                onChange={(e) => patch({ exemptionLegalBasis: e.target.value })}
              />
            ) : null}
          </SectionCard>

          <SectionCard title={t('invoices.compose.sections.additional_data')}>
            <AdditionalDataEditor
              rows={state.additionalData}
              rowErrors={additionalDataErrors}
              disabled={disabled}
              onPatch={patchAdditional}
              onAdd={addAdditional}
              onRemove={removeAdditional}
            />
            <div className="mt-4 space-y-1.5">
              <FieldLabel htmlFor="footer-note">
                {t('invoices.compose.fields.footer_note')}
              </FieldLabel>
              <Textarea
                id="footer-note"
                rows={3}
                disabled={disabled}
                aria-invalid={footerNoteError ? true : undefined}
                aria-describedby={footerNoteError ? 'footer-note-error' : undefined}
                placeholder={t('invoices.compose.fields.footer_note_placeholder')}
                value={state.footerNote}
                onChange={(e) => patch({ footerNote: e.target.value })}
              />
              <FieldError id="footer-note-error" message={footerNoteError} />
            </div>
          </SectionCard>

          {evaluation.computedTotals ? (
            <div className="flex flex-wrap justify-end gap-2">
              {/* A margin invoice discloses no net and no VAT — showing a "net" equal to the gross and a
                  zero VAT would state something the document does not. One amount due is the truth. */}
              {margin ? (
                <AmountPill
                  label={t('invoices.margin.total_due')}
                  value={formatMoney(
                    lng,
                    Number(evaluation.computedTotals.totalGross),
                    evaluation.computedTotals.currency,
                  )}
                />
              ) : (
                <>
                  <AmountPill
                    label={t('invoices.details.net')}
                    value={formatMoney(
                      lng,
                      Number(evaluation.computedTotals.totalNet),
                      evaluation.computedTotals.currency,
                    )}
                  />
                  <AmountPill
                    label={t('invoices.details.vat')}
                    value={formatMoney(
                      lng,
                      Number(evaluation.computedTotals.totalVat),
                      evaluation.computedTotals.currency,
                    )}
                  />
                  <AmountPill
                    label={t('invoices.details.gross')}
                    value={formatMoney(
                      lng,
                      Number(evaluation.computedTotals.totalGross),
                      evaluation.computedTotals.currency,
                    )}
                  />
                </>
              )}
            </div>
          ) : null}
        </div>
      </ScrollArea>
    </div>
  );
}

/** A composer section as a card, matching the KSeF invoice-detail preview's box styling. */
function SectionCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function SaveStatus({ saving, disabled }: { saving: boolean; disabled: boolean }) {
  const { t } = useTranslation();
  if (disabled) return null;
  return (
    <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
      {saving ? (
        <>
          <Loader2 className="size-3 animate-spin" />
          {t('invoices.compose.saving')}
        </>
      ) : (
        <>
          <Check className="size-3" />
          {t('invoices.compose.saved')}
        </>
      )}
    </p>
  );
}

/** Compact readiness state beside the save status; when not ready, hovering lists every issue. */
function StateChip({ issuable, issues }: { issuable: boolean; issues: string[] }) {
  const { t } = useTranslation();
  if (issuable) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
        <CircleCheck className="size-3.5" />
        {t('invoices.compose.panel.ready')}
      </span>
    );
  }
  const chip = (
    <span className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
      <AlertTriangle className="size-3.5" />
      {t('invoices.compose.panel.not_ready')}
    </span>
  );
  if (issues.length === 0) return chip;
  return (
    <Hint
      className="max-w-xs"
      content={
        <ul className="space-y-1">
          {issues.map((issue, i) => (
            <li key={i} className="flex gap-1.5">
              <span aria-hidden>•</span>
              <span>{issue}</span>
            </li>
          ))}
        </ul>
      }
    >
      <button type="button" className="cursor-help outline-none">
        {chip}
      </button>
    </Hint>
  );
}

function ProfileNotReadyBanner() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3.5 py-2.5 text-sm">
      <span className="text-amber-700 dark:text-amber-300">
        {t('invoices.compose.profile_not_ready')}
      </span>
      <Button asChild variant="outline">
        <Link to="/settings">{t('invoices.compose.set_up_profile')}</Link>
      </Button>
    </div>
  );
}

function ConflictBanner({ onReload }: { onReload: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-3.5 py-2.5 text-sm">
      <span className="flex items-center gap-2 text-destructive">
        <AlertTriangle className="size-4 shrink-0" />
        {t('invoices.compose.conflict')}
      </span>
      <Button type="button" variant="outline" onClick={onReload}>
        {t('invoices.compose.reload')}
      </Button>
    </div>
  );
}

function ReadOnlyBanner() {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border bg-muted/40 px-3.5 py-2.5 text-sm text-muted-foreground">
      {t('invoices.compose.read_only')}
    </div>
  );
}

function SaleDateFields({
  state,
  margin,
  procedureError,
  disabled,
  patch,
}: {
  state: EditorState;
  /** Under the margin scheme the sale also carries its art. 119/120 procedure marker. */
  margin: boolean;
  procedureError?: string;
  disabled: boolean;
  patch: (p: Partial<EditorState>) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div
        role="radiogroup"
        aria-label={t('invoices.compose.sale_date.mode')}
        className="grid grid-cols-2 gap-2"
      >
        <ModeButton
          active={state.saleMode === 'day'}
          disabled={disabled}
          onClick={() => patch({ saleMode: 'day' })}
        >
          {t('invoices.compose.sale_date.day')}
        </ModeButton>
        <ModeButton
          active={state.saleMode === 'period'}
          disabled={disabled}
          onClick={() => patch({ saleMode: 'period' })}
        >
          {t('invoices.compose.sale_date.period')}
        </ModeButton>
      </div>
      {state.saleMode === 'day' ? (
        <DateField
          id="sale-on-date"
          label={t('invoices.compose.sale_date.on_date')}
          value={state.saleOnDate}
          disabled={disabled}
          onChange={(v) => patch({ saleOnDate: v })}
        />
      ) : (
        <DateRangeField
          id="sale-period"
          label={t('invoices.compose.sale_date.range')}
          value={{ from: state.salePeriodFrom, to: state.salePeriodTo }}
          disabled={disabled}
          onChange={({ from, to }) => patch({ salePeriodFrom: from, salePeriodTo: to })}
        />
      )}
      {/* The procedure describes the sale, not any single line — it rides below the date here rather
          than heading the lines section, where it read as a property of the items. */}
      {margin ? (
        <div className="space-y-1.5">
          <FieldLabel htmlFor="margin-procedure">
            {t('invoices.margin.procedure')}
          </FieldLabel>
          <MarginProcedurePicker
            value={state.marginProcedure}
            disabled={disabled}
            invalid={Boolean(procedureError)}
            onChange={(procedure) => patch({ marginProcedure: procedure })}
          />
          <FieldError id="margin-procedure-error" message={procedureError} />
        </div>
      ) : null}
    </div>
  );
}

function ModeButton({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded-lg border px-3 py-2 text-sm font-medium outline-none transition-colors disabled:pointer-events-none disabled:opacity-50',
        'focus-visible:ring-2 focus-visible:ring-ring/60',
        active
          ? 'border-primary/40 bg-accent text-foreground ring-1 ring-primary/30'
          : 'border-border text-muted-foreground hover:border-foreground/20 hover:bg-accent/50 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function PaymentFields({
  state,
  disabled,
  patch,
}: {
  state: EditorState;
  disabled: boolean;
  patch: (p: Partial<EditorState>) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <FieldLabel htmlFor="payment-method">
          {t('invoices.compose.fields.payment_method')}
        </FieldLabel>
        <MethodPicker
          value={state.paymentMethod}
          disabled={disabled}
          onChange={(m) => patch({ paymentMethod: m })}
        />
      </div>
      <DateField
        id="payment-due"
        label={t('invoices.compose.fields.due_date')}
        value={state.paymentDueDate}
        disabled={disabled}
        onChange={(v) => patch({ paymentDueDate: v })}
      />
      <DateField
        id="payment-paid"
        label={t('invoices.compose.fields.paid_date')}
        value={state.paymentPaidDate}
        disabled={disabled}
        onChange={(v) => patch({ paymentPaidDate: v })}
      />
      <BankAccountPicker
        value={state.paymentBankAccount}
        disabled={disabled}
        onChange={(account) => patch({ paymentBankAccount: account })}
      />
    </div>
  );
}

/**
 * Picks the seller's payout account from the issuer profile's saved bank accounts, previewing the
 * bank name (when set) plus a couple of significant digits of the number. Empty until the profile has
 * at least one account, with a shortcut to add them.
 */
function BankAccountPicker({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (account: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { data } = useIssuerProfile();
  const accounts = data?.profile?.bankAccounts ?? [];
  const selected = accounts.find((a) => a.account === value) ?? null;

  return (
    <div className="space-y-1.5">
      <FieldLabel htmlFor="payment-account">{t('invoices.compose.fields.bank_account')}</FieldLabel>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            id="payment-account"
            type="button"
            variant="outline"
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className={cn('truncate', value === '' && 'text-muted-foreground')}>
              {value === ''
                ? t('invoices.compose.fields.bank_account_placeholder')
                : formatAccountSummary(value, selected?.bankName ?? null)}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-(--radix-dropdown-menu-trigger-width)">
          {accounts.length === 0 ? (
            <div className="space-y-1 px-2 py-3 text-center">
              <p className="text-sm text-muted-foreground">
                {t('invoices.compose.fields.bank_account_empty')}
              </p>
              <Button asChild variant="link" size="sm">
                <Link to="/settings">{t('invoices.compose.fields.bank_account_add')}</Link>
              </Button>
            </div>
          ) : (
            accounts.map((account) => (
              <DropdownMenuItem
                key={account.account}
                className="gap-2"
                onSelect={() => onChange(account.account)}
              >
                <Check
                  className={cn(
                    'size-4 shrink-0',
                    account.account === value ? 'opacity-100' : 'opacity-0',
                  )}
                />
                <span className="flex min-w-0 flex-col">
                  {account.bankName ? (
                    <span className="truncate text-sm">{account.bankName}</span>
                  ) : null}
                  <span
                    className={cn(
                      'font-mono text-xs break-all',
                      account.bankName ? 'text-muted-foreground' : 'text-sm',
                    )}
                  >
                    {formatAccountFull(account.account)}
                  </span>
                </span>
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** Short "Bank · 1234 … 6789" (or just the masked number) shown on the picker trigger to save space. */
function formatAccountSummary(account: string, bankName: string | null): string {
  const masked = maskAccount(account);
  return bankName ? `${bankName} · ${masked}` : masked;
}

/** Shortens an account to a couple of significant groups: `1234 … 6789`. */
function maskAccount(account: string): string {
  const digits = account.replace(/\s+/g, '');
  if (digits.length <= 8) return digits;
  return `${digits.slice(0, 4)} … ${digits.slice(-4)}`;
}

function MethodPicker({
  value,
  onChange,
  disabled,
}: {
  value: PaymentMethod | '';
  onChange: (method: PaymentMethod) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          id="payment-method"
          type="button"
          variant="outline"
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', value === '' && 'text-muted-foreground')}>
            {value === ''
              ? t('invoices.compose.fields.payment_method_placeholder')
              : t(`invoices.compose.payment_method.${value}`)}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {V1_SELECTABLE_PAYMENT_METHODS.map((method) => (
          <DropdownMenuItem key={method} onSelect={() => onChange(method)}>
            <Check className={cn('size-4', value === method ? 'opacity-100' : 'opacity-0')} />
            {t(`invoices.compose.payment_method.${method}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
